import { IEditorMetadata, IHistoryLogDepricated, ITag, taskOrIssueENUM } from "@mctypes/global.types";
import { IUpdateIssueResp } from "@mctypes/tempTypes";
import { IHistoryLog } from "@schemas/commonSchema";
import { IIssueApi, IIssueGis, INoteboardNode } from "@schemas/issueSchema";
import { GetProjectByProjectIdIssuesOnBoardApiArg, openapi } from "../../../../openapi";
import { apiSlice } from "../../../apiSlice";
import { removeAppModalInRedux, setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { trpcSlice } from "../../../trpcSlice";
import { patchAllIssuesAfterSingleIssueUpdate, updateIssueAfterResp } from "./issueapi.enhance";
import { addMissingKeysIssue, convertHistoryIssueVal } from "./issueUtils";

interface IIssueBoardInput {
  projectId: string;
  boardId: string;
}

interface IIssueQueryInput {
  projectId: string;
  issueId: string;
}

interface IIssueChangeBoardQueryInput extends IIssueQueryInput {
  boardIdToChangeTo: string;
}

export interface IPostIssue {
  projectId: string;
  issueBoardId: string;
  issueTitle: string;
  taskOrIssue: taskOrIssueENUM;
  issueIdIfItIsATask?: string;
  tags?: string[];
  connectedMeeting?: string;
  clientAccess?: boolean;
  gis?: IIssueGis;
  gisImage?: string;
  connectedChecklistItem?: string;
  connectedMeetingAgendaItem?: string;
  noteboardNode?: INoteboardNode;
}

export interface IUpdateIssue {
  projectId: string;
  issueBoardId: any;
  issueId: string;
  attr: string;
  value: any;
  oldValue: any;
  gisImage?: string;
  order?: number;
}

interface IQueryPostComment {
  projectId: string;
  issueId: string;
  text: string;
  metadata: IEditorMetadata;
}

interface IQueryDeleteComment {
  projectId: string;
  issueId: string;
  commentId: string;
}

interface IQueryUpdateComment {
  projectId: string;
  issueId: string;
  text: string;
  metadata: IEditorMetadata;
  commentId: string;
}

export const issueRTKLegacy = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    doUpdateIssue: builder.mutation<IUpdateIssueResp, IUpdateIssue>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/board/${input.issueBoardId}/item/${input.issueId}`,
          method: "PUT",
          body: {
            attr: input.attr,
            value: input.value,
            oldValue: input.oldValue,
            gisImage: input.gisImage,
            order: input.order,
          },
        };
      },
      async onQueryStarted(args, api) {
        const { dispatch, queryFulfilled, getState } = api;
        await patchAllIssuesAfterSingleIssueUpdate(args, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
      invalidatesTags: ["Issue", "Meeting", "IssueFormulas", "IssueLogs"],
    }),
    createIssue: builder.mutation<IIssueApi, IPostIssue>({
      query(postIssue) {
        return {
          url: `/project/${postIssue.projectId}/issue/board/${postIssue.issueBoardId}/type/${postIssue.taskOrIssue}`,
          method: "POST",
          body: {
            title: postIssue.issueTitle,
            issueId: postIssue.issueIdIfItIsATask,
            tags: postIssue.tags,
            connectedMeeting: postIssue.connectedMeeting,
            clientAccess: postIssue.clientAccess,
            gis: postIssue.gis,
            gisImage: postIssue.gisImage,
            connectedChecklistItem: postIssue.connectedChecklistItem,
            connectedMeetingAgendaItem: postIssue.connectedMeetingAgendaItem,
            noteboardNode: postIssue.noteboardNode,
          },
        };
      },
      async onQueryStarted({ issueTitle, ...createIssueParams }, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const createdIssue = addMissingKeysIssue(resp.data, "");
          const projectId = createIssueParams.projectId;
          const issueBoardId = createIssueParams.issueBoardId;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                if (createIssueParams.taskOrIssue === "ISSUE") {
                  draft.push(createdIssue as any);
                } else if (createIssueParams.taskOrIssue === "TASK") {
                  const parent = draft.find((i) => i._id === createIssueParams.issueIdIfItIsATask);
                  if (parent) {
                    parent.connectedTasks ??= [];
                    // Only add if not already present
                    if (!parent.connectedTasks.some((t) => t._id === createdIssue._id)) {
                      parent.connectedTasks.push(createdIssue as any);
                    }
                  } else {
                    // Fallback: if parent not found, add as top-level to prevent missing
                    if (!draft.some((i) => i._id === createdIssue._id)) {
                      draft.push(createdIssue as any);
                    }
                  }
                }
              }),
            );
          }

          dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.ISSUE_ADDED));
          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
          if (createIssueParams.connectedMeetingAgendaItem != null) {
            dispatch(trpcSlice.util.invalidateTags(["Meeting", "MeetingAgenda"]));
          }
        } catch (err) {
          console.warn("createIssue.onQueryStarted handled error:", err);
        }
      },
      invalidatesTags: ["Issue", "Meeting", "IssueFormulas"],
    }),
    connectIssueOrTaskFromOtherBoardsToIssue: builder.mutation<
      IUpdateIssueResp,
      { projectId: string; issueOrTaskId: string; issueId: string; connectOrDisconnect: "CONNECT" | "DISCONNECT" }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/task/connect-disconnect-issue-or-task-from-other-boards`,
          method: "POST",
          body: {
            issueOrTaskId: input.issueOrTaskId,
            issueId: input.issueId,
            connectOrDisconnect: input.connectOrDisconnect,
          },
        };
      },
      async onQueryStarted(args, api) {
        await patchAllIssuesAfterSingleIssueUpdate(args, api);
      },
      invalidatesTags: ["Issue"],
    }),
    getUniqueTagsOnBoard: builder.query<ITag[], IIssueBoardInput>({
      query: (input) => `/project/${input.projectId}/issue/board/${input.boardId}/tags`,
      providesTags: ["SingleIssueBoard", "Tags"],
    }),

    addIssueComment: builder.mutation<IIssueApi, IQueryPostComment>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/comment`,
          method: "POST",
          body: { text: input.text, metadata: input.metadata },
        };
      },
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState as any, dispatch);
      },
      invalidatesTags: ["Issue"],
    }),
    deleteIssueComment: builder.mutation<IIssueApi, IQueryDeleteComment>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/comment/${input.commentId}`,
          method: "DELETE",
        };
      },
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState as any, dispatch);
      },
      invalidatesTags: ["Issue"],
    }),

    updateIssueComment: builder.mutation<IIssueApi, IQueryUpdateComment>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/comment/${input.commentId}`,
          method: "PUT",
          body: { text: input.text, metadata: input.metadata },
        };
      },
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState as any, dispatch);
      },
      invalidatesTags: ["Issue"],
    }),
    toggleClientAccess: builder.mutation<void, IIssueQueryInput>({
      query(input) {
        return { url: `/project/${input.projectId}/issue/item/${input.issueId}/clientaccess`, method: "PUT" };
      },
      invalidatesTags: ["Issue"],
    }),

    getIssueHistoryLogs: builder.query<IHistoryLog[], IIssueQueryInput>({
      query: (input) => `/project/${input.projectId}/issue/log/item/${input.issueId}`,
      transformResponse: (resp: IHistoryLogDepricated[]) =>
        resp.map((issue) => ({
          _id: issue._id,
          dataField: issue.column,
          columnDisplayName: issue.column,
          createdAt: issue.createdAt,
          newValue: convertHistoryIssueVal(issue.newValue),
          oldValue: convertHistoryIssueVal(issue.oldValue),
          updatedAt: issue.updatedAt,
          editedBy: issue?.user?.name ?? issue.userName,
        })),
      providesTags: ["IssueLogs"],
    }),

    changeBoardOfIssueItem: builder.mutation<IIssueApi, IIssueChangeBoardQueryInput>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/board`,
          method: "PUT",
          body: { boardIdToChangeTo: input.boardIdToChangeTo },
        };
      },
      async onQueryStarted({ issueId }, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const projectId = resp.data.project;
          const issueBoardId = resp.data.issueBoard as string;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                const index = draft.findIndex((issue) => issue._id === issueId);
                if (index >= 0) draft.splice(index, 1);
              }),
            );
          }

          dispatch(removeAppModalInRedux(issueId));
          dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
        } catch (err) {
          console.warn("changeBoardOfIssueItem.onQueryStarted handled error:", err);
        }
      },
      invalidatesTags: ["Issue"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateIssueMutation,
  useDoUpdateIssueMutation,
  useConnectIssueOrTaskFromOtherBoardsToIssueMutation,
  useGetUniqueTagsOnBoardQuery,
  useAddIssueCommentMutation,
  useDeleteIssueCommentMutation,
  useUpdateIssueCommentMutation,
  useToggleClientAccessMutation,
  useGetIssueHistoryLogsQuery,
  useChangeBoardOfIssueItemMutation,
} = issueRTKLegacy;

/** Plocka args för doGetAllIssues från RTK Query-cachen.
 *  - Fungerar oavsett RootState-typ
 *  - Fungerar oavsett reducerPath (t.ex. "api", "openapi"…)
 */
function getDoGetAllIssuesArgs(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
  //predicate?: (args: GetProjectByProjectIdIssuesOnBoardApiArg) => boolean,
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  const state = typeof stateOrGetState === "function" ? (stateOrGetState as () => unknown)() : stateOrGetState;
  const queries = (state as any)?.[api.reducerPath]?.queries ?? {};
  const out: GetProjectByProjectIdIssuesOnBoardApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdIssuesOnBoard(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      const args = JSON.parse(json) as GetProjectByProjectIdIssuesOnBoardApiArg;
      out.push(args);
    } catch {
      // ignore parse error
    }
  }
  return out;
}

/** Hjälper att filtrera på ett specifikt board. */
function getDoGetAllIssuesArgsForBoard(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
  projectId: string,
  issueBoardId: string,
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  return getDoGetAllIssuesArgs(stateOrGetState, api);
}
